// One entity's audit trail, ported from the vouchers admin's
// entity-history-rail.tsx. "tab" renders inside a detail page tab (kore Tabs
// unmount inactive panels, so it fetches only while shown); "rail" is a
// collapsed section that fetches once expanded. The sheet is local state; the
// audit page owns `?eventId=`, reached through "Open in audit log", and shows
// the full trail past the first page through "View all in audit log".

import { AuditTimeline } from "@pej/kore/components";
import type { AuditEvent } from "@pej/kore/types";
import { Link } from "@tanstack/react-router";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useCallback, useId, useMemo, useState } from "react";
import { useAuditSearch } from "@/api/audit";
import type { AuditTargetType } from "@/api/types";
import { useWorkspaceContext } from "@/contexts/workspace-context";
import { AuditEventSheet } from "./audit-event-sheet";
import { targetTimelineRq } from "./audit-filters";
import { summarizeKoreEvent, toKoreAuditEvent } from "./kore-audit-adapter";

export interface EntityHistoryProps {
    targetType: AuditTargetType;
    targetId: string;
    variant: "tab" | "rail";
}

export function EntityHistory({ targetType, targetId, variant }: EntityHistoryProps) {
    const { workspaceId } = useWorkspaceContext();
    const [railOpen, setRailOpen] = useState(false);
    const [eventId, setEventId] = useState<string | null>(null);
    const panelId = useId();
    const open = variant === "tab" || railOpen;

    const historyQuery = useAuditSearch(workspaceId, targetTimelineRq(targetType, targetId), {
        enabled: open,
        keepPrevious: false,
    });
    const events = useMemo(() => (historyQuery.data?.items ?? []).map(toKoreAuditEvent), [historyQuery.data]);

    const closeSheet = useCallback(() => setEventId(null), []);
    const renderMetadata = useCallback(
        (event: AuditEvent) => (
            <Link
                to="/w/$workspaceId/audit"
                params={{ workspaceId }}
                search={{ eventId: event.eventId }}
                className="mt-3 inline-block text-sm font-medium text-kore-accent hover:underline"
            >
                Open in audit log
            </Link>
        ),
        [workspaceId],
    );

    const timeline = (
        <>
            <AuditTimeline
                events={events}
                currentEventId={eventId ?? undefined}
                loading={historyQuery.isLoading}
                // Fast fetches never flash a skeleton.
                loadingDelayMs={250}
                onSelect={setEventId}
                summarize={summarizeKoreEvent}
            />
            {historyQuery.data?.nextPageToken ? (
                <Link
                    to="/w/$workspaceId/audit"
                    params={{ workspaceId }}
                    search={{ targetTypes: targetType, targetIds: targetId }}
                    className="mt-3 inline-block text-sm font-medium text-kore-accent hover:underline"
                >
                    View all in audit log
                </Link>
            ) : null}
        </>
    );

    return (
        <section className={variant === "rail" ? "mt-2" : undefined}>
            {variant === "rail" ? (
                <>
                    <button
                        type="button"
                        aria-expanded={railOpen}
                        aria-controls={panelId}
                        onClick={() => setRailOpen((o) => !o)}
                        className="flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground"
                    >
                        {railOpen ? (
                            <ChevronDown aria-hidden="true" className="size-3.5" />
                        ) : (
                            <ChevronRight aria-hidden="true" className="size-3.5" />
                        )}
                        History
                    </button>
                    {railOpen ? (
                        <div id={panelId} className="mt-3 max-w-2xl">
                            {timeline}
                        </div>
                    ) : null}
                </>
            ) : (
                <div className="max-w-2xl">{timeline}</div>
            )}
            <AuditEventSheet
                workspaceId={workspaceId}
                eventId={eventId}
                onNavigate={setEventId}
                onClose={closeSheet}
                onNotFound={closeSheet}
                renderMetadata={renderMetadata}
            />
        </section>
    );
}
